import type { OperationVariables, QueryResult, Unmasked } from '@apollo/client';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { useState } from 'react';
import { AlphaTopicActivityAction } from '../graphql/generated/graphql';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const ACTIVITIES_PAGE_SIZE = 100;
export const ACTIVITIES_END_OF_TIME = '2099-12-31T00:00:00.0Z';
export const ALL_TOPIC_ACTIVITIES_TYPES = Object.values(
  AlphaTopicActivityAction,
);

const useFetchMoreActivities = <
  TKey extends string,
  TData extends { [key in TKey]: { actedAt: string }[] },
  TVariables extends OperationVariables,
>(
  response: Pick<
    QueryResult<TData, TVariables>,
    'loading' | 'variables' | 'fetchMore' | 'data'
  >,
  field: TKey,
  variables?: Partial<TVariables>,
): (() => Promise<void>) | null => {
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [isLoadingPrev, setIsLoadingPrev] = useState(response.loading);

  const getActivities = (item: unknown) => {
    if (item != null && typeof item === 'object' && field in item) {
      return item[field as keyof typeof item] as { actedAt: string }[];
    }
    return [];
  };

  if (isLoadingPrev !== response.loading) {
    setIsLoadingPrev(response.loading);
    setHasMoreActivities(
      getActivities(response.data).length >= ACTIVITIES_PAGE_SIZE,
    );
  }

  const fetchMoreActivities = async (): Promise<void> => {
    const lastActivity = getActivities(response.data).at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two items with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await response.fetchMore({
      variables: {
        ...response.variables,
        ...variables,
        beforeAt,
      },
      updateQuery(
        previousResult: Unmasked<TData>,
        { fetchMoreResult }: { fetchMoreResult: Unmasked<TData> },
      ): Unmasked<TData> {
        const prevItems = getActivities(previousResult);
        const newItems = getActivities(fetchMoreResult);

        setHasMoreActivities(newItems.length >= ACTIVITIES_PAGE_SIZE);

        const firstOverFetchedItemIndex = prevItems.findIndex((item) =>
          isEqual(item, newItems[0]),
        );

        const prevItemsWithoutOverFetched =
          firstOverFetchedItemIndex !== -1
            ? prevItems.slice(0, firstOverFetchedItemIndex)
            : prevItems;

        return {
          ...previousResult,
          [field]: [...prevItemsWithoutOverFetched, ...newItems],
        };
      },
    });
  };

  return hasMoreActivities && !response.loading ? fetchMoreActivities : null;
};

export default useFetchMoreActivities;
